import type { ComponentProps } from 'react'
import { useRef, useState, useEffect } from 'react'
import { Check, Copy } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from './button'

export type CopyButtonProps = ComponentProps<typeof Button> & {
  content: string
  label?: string
  onCopy?: () => void
  onError?: (error: Error) => void
  timeout?: number
}

export function CopyButton({
  content,
  label = 'Copy to clipboard',
  onCopy,
  onError,
  timeout = 1500,
  className,
  children,
  onClick,
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleCopy = async () => {
    if (typeof window === 'undefined' || !navigator?.clipboard?.writeText) {
      onError?.(new Error('Clipboard API not supported'))
      return
    }

    if (copied) return

    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      onCopy?.()

      timeoutRef.current = setTimeout(() => {
        setCopied(false)
      }, timeout)
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error(String(error)))
    }
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const Icon = copied ? Check : Copy

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn('hover:bg-transparent h-auto w-auto p-0', className)}
      onClick={async (e) => {
        await handleCopy()
        onClick?.(e)
      }}
      title={label}
      aria-label={label}
      {...props}>
      {children ?? <Icon className="size-4" />}
    </Button>
  )
}
